import { CustomSelect } from 'components/common/CustomSelect/CustomSelect';
import { PostCardRequestData } from 'lib/services/cardsServices';
import { CardMediaTypes } from 'lib/types/entities';
import {
  Control,
  FormState,
  UseFormClearErrors,
  UseFormRegister,
  UseFormWatch,
} from 'react-hook-form';
import {
  CARD_TYPE_OPTIONS,
  FREQUENCY_OPTIONS,
  SIMILIATITY_OPTIONS,
} from './cardOptions';
import { FC, useEffect } from 'react';
import { CardCraftSentence } from '../CardCraftSentence';
import { CardInitialUser } from '../CardInitialUser';

export type CardFormShape = PostCardRequestData;
type CreateTabFormProps = {
  register: UseFormRegister<CardFormShape>;
  formState: FormState<CardFormShape>;
  updateValue: (
    fieldName: keyof CardFormShape,
    value: string | boolean | number,
    shouldDirty?: boolean,
  ) => void;
  clearErrors: UseFormClearErrors<CardFormShape>;
  watch: UseFormWatch<CardFormShape>;
  control: Control<CardFormShape>;
  isCreating: boolean;
  isUsed?: boolean;
};

export const CardForm: FC<CreateTabFormProps> = ({
  register,
  formState,
  updateValue,
  clearErrors,
  watch,
  control,
  isUsed = false,
  isCreating = false,
}) => {
  const isAdvanced = watch('advanced');
  const isPromptTitleShow = watch('promptTitleShow');
  const isInitialUser = Boolean(watch('initialUser'));

  const isLimitActive =
    !!watch('responseType') && watch('responseType') === 'text';
  const isCraft =
    !!watch('mediaType') && watch('mediaType') === CardMediaTypes.craft;

  useEffect(() => {
    if (isCraft) {
      updateValue('responseType', 'text');
    }
  }, [isCraft]);

  const updateFieldValue = (
    fieldName: keyof CardFormShape,
    value: string | boolean | number,
  ) => {
    updateValue(fieldName, value, true);
  };

  return (
    <form className="w-[800px] overflow-y-auto max-h-[800px]">
      <div className="grid grid-cols-2 gap-5 mb-2">
        <div className="flex flex-col space-y-2">
          <div className="w-full">
            <label className="text-sm text-gray-400" htmlFor="email">
              Tab title
            </label>
            <input
              type="text"
              placeholder="Title"
              {...register('title')}
              className="w-full rounded p-2 pb-2.5 selected:bg-white border-gray-200 border-2 disabled:text-gray-400 disabled:bg-gray-100"
            />
            {formState?.errors.title?.message && (
              <p className="text-red-500 italic text-xs">
                {formState?.errors.title?.message}
              </p>
            )}
          </div>
          <CustomSelect
            label={
              !isCreating
                ? 'Prompt Media'
                : 'Prompt Media (text & audio can be set after tab creation)'
            }
            name="mediaType"
            isDisabled={!isCreating && isCraft}
            control={control}
            options={[
              { value: CardMediaTypes.text, label: 'Text' },
              {
                value: CardMediaTypes.audio_text,
                label: 'Text & Audio',
                isDisabled: isCreating,
              },
              {
                value: CardMediaTypes.craft,
                label: 'Craft sentence',
                isDisabled: !isCreating,
              },
            ]}
            error={formState?.errors.mediaType?.message}
          />
          <CustomSelect
            label="Status"
            name="active"
            control={control}
            options={[
              { value: true, label: 'Active' },
              { value: false, label: 'Inactive' },
            ]}
            error={formState?.errors.active?.message}
          />
          <div className="w-full">
            <label htmlFor="advanced" className="text-sm text-gray-400">
              Advanced?
            </label>
            <div className="flex mb-3">
              <div
                onClick={() => updateFieldValue('advanced', true)}
                className="h-11 border border-gray-300 px-2 py-1 rounded w-full mr-2 flex items-center justify-between"
              >
                <p>Yes</p>
                <input
                  type="checkbox"
                  name="isAdvanced"
                  onChange={() => {}}
                  checked={isAdvanced}
                />
              </div>
              <div
                onClick={() => updateFieldValue('advanced', false)}
                className="h-11 border border-gray-300 px-2 py-1 rounded w-full flex items-center justify-between"
              >
                <p>No</p>
                <input
                  type="checkbox"
                  name="notAdvanced"
                  onChange={() => {}}
                  checked={!isAdvanced}
                />
              </div>
            </div>
            {isLimitActive && (
              <div className="w-full relative mb-1">
                <label
                  className="text-sm text-gray-400"
                  htmlFor="responseLimitNum"
                >
                  Response Limit
                </label>
                <div className="w-full flex justify-between space-x-2">
                  <input
                    id="responseLimitNum"
                    type="number"
                    min={0}
                    placeholder="100"
                    {...register('responseLimitNum')}
                    className="w-1/2 rounded p-2 selected:bg-white border-gray-200 border-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:text-gray-400 disabled:bg-gray-100"
                  />
                  <select
                    id="responseLimitType"
                    {...register('responseLimitType')}
                    className="w-1/2 rounded px-2 py-2.5 bg-white border-gray-200 border-2 disabled:text-gray-400 disabled:bg-gray-100"
                  >
                    <option value="word">Words</option>
                    <option value="character">Characters</option>
                  </select>
                </div>
                {formState?.errors.responseLimitNum?.message && (
                  <p className="text-red-500 italic text-xs absolute">
                    {formState?.errors.responseLimitNum?.message}
                  </p>
                )}
                {formState?.errors.responseLimitType?.message && (
                  <p className="text-red-500 italic text-xs absolute">
                    {formState?.errors.responseLimitType?.message}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-col space-y-2">
          <CustomSelect
            label="Card type"
            name="type"
            isDisabled={isUsed}
            control={control}
            options={CARD_TYPE_OPTIONS}
            error={formState?.errors.type?.message}
          />
          <CustomSelect
            label="Response Media"
            name="responseType"
            control={control}
            isDisabled={isUsed}
            options={[
              { value: 'text', label: 'Text' },
              { value: 'photo', label: 'Photo', isDisabled: isCraft },
            ]}
            error={formState?.errors.responseType?.message}
          />
          <CustomSelect
            label="Frequency"
            name="frequency"
            control={control}
            options={FREQUENCY_OPTIONS}
            error={formState?.errors.frequency?.message}
          />
          <CustomSelect
            label="Similarity group"
            name="similiatityGroup"
            control={control}
            options={SIMILIATITY_OPTIONS}
            error={formState?.errors.similiatityGroup?.message}
          />
        </div>
      </div>
      <div className="flex flex-col space-y-2 mb-4 pb-4 border-b border-b-gray-200">
        <div className="grid grid-cols-2 gap-5">
          <div className="w-full">
            <label className="text-sm text-gray-400" htmlFor="promtTitle">
              Prompt
            </label>
            <input
              type="text"
              placeholder="How do you feel today?"
              {...register('promptTitle')}
              className="w-full rounded p-2 selected:bg-white border-gray-200 border-2 disabled:text-gray-400 disabled:bg-gray-100"
            />
            {formState?.errors.promptTitle?.message && (
              <p className="text-red-500 italic text-xs">
                {formState?.errors.promptTitle.message}
              </p>
            )}
          </div>
          <div>
            <label htmlFor="advanced" className="text-sm text-gray-400">
              Show Prompt?
            </label>
            <div className="flex mb-3">
              <div
                onClick={() => updateFieldValue('promptTitleShow', true)}
                className="h-11 border border-gray-300 px-2 py-1 rounded w-full mr-2 flex items-center justify-between"
              >
                <p>Yes</p>
                <input
                  type="checkbox"
                  name="promptTitleShow"
                  onChange={() => {}}
                  checked={isPromptTitleShow}
                />
              </div>
              <div
                onClick={() => updateFieldValue('promptTitleShow', false)}
                className="h-11 border border-gray-300 px-2 py-1 rounded w-full flex items-center justify-between"
              >
                <p>No</p>
                <input
                  type="checkbox"
                  name="promptTitleNotShow"
                  onChange={() => {}}
                  checked={!isPromptTitleShow}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="w-full">
          <label className="text-sm text-gray-400" htmlFor="promptFinishedTab">
            Prompt (Finished Tab)
          </label>
          <input
            type="text"
            placeholder="Write the first thing that comes to your mind."
            {...register('promptFinishTab')}
            className="w-full rounded p-2 selected:bg-white border-gray-200 border-2 disabled:text-gray-400 disabled:bg-gray-100"
          />
          {formState?.errors.promptFinishTab?.message && (
            <p className="text-red-500 italic text-xs">
              {formState?.errors.promptFinishTab?.message}
            </p>
          )}
        </div>
        <div className="w-full">
          <label className="text-sm text-gray-400" htmlFor="sbtextStarter">
            Subtext - Starter
          </label>
          <input
            type="text"
            placeholder="My hair looks great today!"
            {...register('subtextStarter')}
            className="w-full rounded p-2 selected:bg-white border-gray-200 border-2 disabled:text-gray-400 disabled:bg-gray-100"
          />
          {formState?.errors.subtextStarter?.message && (
            <p className="text-red-500 italic text-xs">
              {formState?.errors.subtextStarter?.message}
            </p>
          )}
        </div>
        <div className="w-full">
          <label className="text-sm text-gray-400" htmlFor="subtextRegular">
            Subtext - Regular
          </label>
          <input
            type="text"
            placeholder="Type or dictate your response here."
            {...register('subtextRegular')}
            className="w-full rounded p-2 selected:bg-white border-gray-200 border-2 disabled:text-gray-400 disabled:bg-gray-100"
          />
          {formState?.errors.subtextRegular?.message && (
            <p className="text-red-500 italic text-xs ">
              {formState?.errors.subtextRegular?.message}
            </p>
          )}
        </div>
      </div>
      {isCraft && (
        <div className="grid grid-cols-2 gap-x-5 mb-4 pb-4 border-b border-b-gray-200">
          <div className="">
            <label htmlFor="isInitialUser" className="text-sm text-gray-400">
              Initial User Input?
            </label>
            <div className="flex mb-3">
              <div
                onClick={() => updateFieldValue('initialUser', true)}
                className="h-11 border border-gray-300 px-2 py-1 rounded w-full mr-2 flex items-center justify-between"
              >
                <p>Yes</p>
                <input
                  type="checkbox"
                  name="isInitialUser"
                  onChange={() => {}}
                  checked={isInitialUser}
                />
              </div>
              <div
                onClick={() => updateFieldValue('initialUser', false)}
                className="h-11 border border-gray-300 px-2 py-1 rounded w-full flex items-center justify-between"
              >
                <p>No</p>
                <input
                  type="checkbox"
                  name="isNotInitialUser"
                  onChange={() => {}}
                  checked={!isInitialUser}
                />
              </div>
            </div>
          </div>
          <div className="col-span-2">
            {isInitialUser && (
              <CardInitialUser
                control={control}
                watch={watch}
                register={register}
                formState={formState}
                clearErrors={clearErrors}
                updateValue={updateValue}
              />
            )}
          </div>
        </div>
      )}
      {isCraft && (
        <CardCraftSentence
          control={control}
          watch={watch}
          formState={formState}
          clearErrors={clearErrors}
          updateValue={updateValue}
        />
      )}
    </form>
  );
};
